import axios from "axios";
import { isDevToolsOpen } from "../BrowserPermissons/useDisableDevTools";

// Helper function to handle logout
const handleLogoutCleanup = () => {
  localStorage.clear();
  // Dispatch custom event to notify components about logout
  window.dispatchEvent(new Event('logout'));
  window.location.href = "/";
};

// Create an Axios instance
const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
  timeout: 60 * 1000,
});

// Request Interceptor
let reqCount = 0;
axiosInstance.interceptors.request.use(
  (config) => {
    const isProd = process.env.REACT_APP_NODE_ENV === "production";
      if (isProd && isDevToolsOpen()) {
      document.body.innerHTML =
        "<h2 style='text-align:center;margin-top:20%;font-family:sans-serif;'>Developer Tools is not allowed</h2>";
      return Promise.reject(new axios.Cancel("DevTools open — request blocked"));
    }
    reqCount += 1;
    const token = localStorage.getItem("authToken");
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);
let isRefreshing = false;
let refreshPromise = null;
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const url = originalRequest.url;
    if (error.response?.status === 401) {
      if (url.includes("/Auth/refreshtoken")) {
        handleLogoutCleanup();
        return Promise.reject("Refresh token failed. Redirecting to login.");
      }
      if (!isRefreshing) {
        isRefreshing = true;
        refreshPromise = new Promise(async (resolve, reject) => {
          try {
            const userInfo = JSON.parse(
              localStorage.getItem("Authentication") || "{}"
            );
            const refreshResponse = await axiosInstance.post(
              `/Auth/refreshtoken`,
              { token: userInfo.refresh_token },
              {
                headers: {
                  sessionId: userInfo.id_token,
                  token: userInfo.refresh_token,
                  "Content-Type": "application/json",
                },
              }
            );

            if (refreshResponse.status === 200) {
              const { access_token, refresh_token, id_token } =
                refreshResponse.data.Data;

              const updatedAuthenticationData = {
                ...userInfo,
                access_token,
                refresh_token,
                id_token,
              };
              localStorage.setItem(
                "Authentication",
                JSON.stringify(updatedAuthenticationData)
              );
              // Dispatch storage event to notify DataProvider of token update
              window.dispatchEvent(new StorageEvent('storage', {
                key: 'Authentication',
                newValue: JSON.stringify(updatedAuthenticationData)
              }));
              axiosInstance.defaults.headers.common[
                "Authorization"
              ] = `Bearer ${access_token}`;
              resolve(access_token); 
            } else {
              throw new Error("Failed to refresh token");
            }
          } catch (refreshError) {
            handleLogoutCleanup();
            reject(refreshError);
          } finally {
            isRefreshing = false;
            refreshPromise = null;
          }
        });
      }
      try {
        const newAccessToken = await refreshPromise;
        originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;
